import React from 'react'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Wand2, Settings2, Loader2, Film } from 'lucide-react'

export interface AIGenerationBaseDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void

    dialogTitle: string
    dialogDescription: string

    mode: 'auto' | 'custom' | null
    onModeChange: (mode: 'auto' | 'custom' | null) => void

    isLoading?: boolean
    loadingTitle?: string
    loadingDescription?: string

    autoPilotTitle?: string
    autoPilotDescription: string
    onAutoPilotClick: () => void

    customDirectorTitle?: string
    customDirectorDescription: string

    customStepTitle?: string
    customStepDescription?: string

    onCustomGenerate: () => void
    customGenerateLabel: string

    children: React.ReactNode
}

export const AIGenerationBaseDialog: React.FC<AIGenerationBaseDialogProps> = ({
    open,
    onOpenChange,
    dialogTitle,
    dialogDescription,
    mode,
    onModeChange,
    isLoading = false,
    loadingTitle = "Generating...",
    loadingDescription = "Please wait while AI generates your content",
    autoPilotTitle = "AI Auto-Pilot",
    autoPilotDescription,
    onAutoPilotClick,
    customDirectorTitle = "Custom Director",
    customDirectorDescription,
    customStepTitle = "Custom Director Settings",
    customStepDescription = "Fine-tune your generation",
    onCustomGenerate,
    customGenerateLabel,
    children
}) => {
    const handleClose = () => {
        onModeChange(null)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[700px] p-0 overflow-hidden bg-background/95 backdrop-blur-xl">
                <DialogHeader className="sr-only">
                    <DialogTitle>{dialogTitle}</DialogTitle>
                    <DialogDescription>{dialogDescription}</DialogDescription>
                </DialogHeader>

                {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-[400px] space-y-4">
                        <Loader2 className="h-12 w-12 animate-spin text-primary" />
                        <div className="text-center space-y-1">
                            <h3 className="text-lg font-medium">{loadingTitle}</h3>
                            <p className="text-sm text-muted-foreground">{loadingDescription}</p>
                        </div>
                    </div>
                ) : !mode ? (
                    // Selection Step
                    <div className="grid md:grid-cols-2 h-[450px]">
                        <div
                            className="bg-muted/30 hover:bg-muted/50 transition-colors p-8 flex flex-col items-center justify-center text-center cursor-pointer border-r border-border/50 group"
                            onClick={onAutoPilotClick}
                        >
                            <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Wand2 className="h-10 w-10 text-primary" />
                            </div>
                            <h3 className="text-2xl font-bold mb-2">{autoPilotTitle}</h3>
                            <p className="text-muted-foreground px-4">
                                {autoPilotDescription}
                            </p>
                        </div>

                        <div
                            className="bg-background hover:bg-muted/30 transition-colors p-8 flex flex-col items-center justify-center text-center cursor-pointer group"
                            onClick={() => onModeChange('custom')}
                        >
                            <div className="h-20 w-20 rounded-full bg-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Settings2 className="h-10 w-10 text-secondary-foreground" />
                            </div>
                            <h3 className="text-2xl font-bold mb-2">{customDirectorTitle}</h3>
                            <p className="text-muted-foreground px-4">
                                {customDirectorDescription}
                            </p>
                        </div>
                    </div>
                ) : (
                    // Custom Configuration Step
                    <div className="p-6">
                        <div className="mb-6 flex items-center justify-between">
                            <div>
                                <h2 className="text-xl font-bold">{customStepTitle}</h2>
                                <p className="text-sm text-muted-foreground">{customStepDescription}</p>
                            </div>

                        </div>

                        <div className="space-y-6">
                            {children}

                            <div className="flex justify-end gap-2 pt-4 border-t">
                                <Button variant="outline" onClick={() => onModeChange(null)}>Back</Button>
                                <Button variant="outline" onClick={() => onModeChange(null)}>Cancel</Button>
                                <Button onClick={onCustomGenerate}>
                                    <Film className="mr-2 h-4 w-4" />
                                    {customGenerateLabel}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
